/**
 * Plain ES5 source, inlined into the Datadog RUM and Logs init scripts in `app/layout.tsx`, that removes
 * care-partner link tokens (the only credential for that questionnaire) from telemetry before it is sent:
 * - `/care-partner/l/:token` path segments in URLs, paths, and free text become `/care-partner/l/[redacted]`
 * - `/api/external/care_partner_requests/:token` segments become `/api/external/care_partner_requests/[redacted]`
 *   so resource timing for those fetches still shows the route without the credential
 * - Session Replay Meta `href` is taken from `location.href` on a separate intake that skips RUM
 *   `beforeSend`. Location href is unforgeable, so `nileInstallCarePartnerSessionReplayHrefScrub`
 *   wraps `JSON.stringify` (and string transport bodies) before the Datadog CDN loads: Meta records
 *   are stringified, then compressed and uploaded — redacting at stringify strips the credential
 *   while leaving Session Replay recording on.
 *
 * It declares `nileScrubCarePartnerRumEvent(event)`, `nileScrubCarePartnerLogEvent(log)`, and
 * `nileInstallCarePartnerSessionReplayHrefScrub()`, which mutate in place and never throw. The inline
 * scripts cannot import modules, so this string is the single shared source.
 */
export const CARE_PARTNER_TELEMETRY_SCRUB_SCRIPT = String.raw`
function nileRedactCarePartnerLinkToken(value) {
    if (typeof value !== 'string') return value;
    return value
        .replace(/(\/care-partner\/l\/)[^\/?#\s"'<>)]+/gi, '$1[redacted]')
        .replace(/(\/api\/external\/care_partner_requests\/)[^\/?#\s"'<>)]+/gi, '$1[redacted]');
}
function nileScrubCarePartnerField(holder, key) {
    if (!holder || typeof holder[key] !== 'string') return;
    holder[key] = nileRedactCarePartnerLinkToken(holder[key]);
}
function nileScrubCarePartnerRumEvent(event) {
    try {
        if (!event) return;
        if (event.view) {
            nileScrubCarePartnerField(event.view, 'url');
            nileScrubCarePartnerField(event.view, 'referrer');
        }
        nileScrubCarePartnerField(event.resource, 'url');
        if (event.error) {
            nileScrubCarePartnerField(event.error, 'message');
            nileScrubCarePartnerField(event.error, 'stack');
            if (event.error.resource) nileScrubCarePartnerField(event.error.resource, 'url');
        }
    } catch (error) {}
}
function nileScrubCarePartnerLogEvent(log) {
    try {
        if (!log) return;
        nileScrubCarePartnerField(log, 'message');
        if (log.error) {
            nileScrubCarePartnerField(log.error, 'message');
            nileScrubCarePartnerField(log.error, 'stack');
        }
        nileScrubCarePartnerField(log.http, 'url');
        if (log.view) {
            nileScrubCarePartnerField(log.view, 'url');
            nileScrubCarePartnerField(log.view, 'referrer');
        }
    } catch (error) {}
}
function nileCarePartnerTelemetryPayloadNeedsScrub(value) {
    return typeof value === 'string' && (value.indexOf('/care-partner/l/') !== -1 || value.indexOf('/care_partner_requests/') !== -1);
}
function nileInstallCarePartnerSessionReplayHrefScrub() {
    try {
        if (typeof JSON !== 'undefined' && typeof JSON.stringify === 'function' && !JSON.stringify.__nileCarePartnerHrefScrub) {
            var originalStringify = JSON.stringify;
            function scrubbedStringify(value, replacer, space) {
                var result = originalStringify(value, replacer, space);
                return nileCarePartnerTelemetryPayloadNeedsScrub(result) ? nileRedactCarePartnerLinkToken(result) : result;
            }
            scrubbedStringify.__nileCarePartnerHrefScrub = true;
            JSON.stringify = scrubbedStringify;
        }
        if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function' && !navigator.sendBeacon.__nileCarePartnerHrefScrub) {
            var originalSendBeacon = navigator.sendBeacon.bind(navigator);
            function scrubbedSendBeacon(url, data) {
                if (typeof data === 'string' && nileCarePartnerTelemetryPayloadNeedsScrub(data)) {
                    data = nileRedactCarePartnerLinkToken(data);
                }
                return originalSendBeacon(url, data);
            }
            scrubbedSendBeacon.__nileCarePartnerHrefScrub = true;
            navigator.sendBeacon = scrubbedSendBeacon;
        }
        if (typeof window !== 'undefined' && typeof window.fetch === 'function' && !window.fetch.__nileCarePartnerHrefScrub) {
            var originalFetch = window.fetch.bind(window);
            function scrubbedFetch(input, init) {
                if (init && typeof init.body === 'string' && nileCarePartnerTelemetryPayloadNeedsScrub(init.body)) {
                    init = Object.assign({}, init, { body: nileRedactCarePartnerLinkToken(init.body) });
                }
                return originalFetch(input, init);
            }
            scrubbedFetch.__nileCarePartnerHrefScrub = true;
            window.fetch = scrubbedFetch;
        }
        if (typeof XMLHttpRequest !== 'undefined' && XMLHttpRequest.prototype && !XMLHttpRequest.prototype.send.__nileCarePartnerHrefScrub) {
            var originalXhrSend = XMLHttpRequest.prototype.send;
            function scrubbedXhrSend(body) {
                if (typeof body === 'string' && nileCarePartnerTelemetryPayloadNeedsScrub(body)) {
                    body = nileRedactCarePartnerLinkToken(body);
                }
                return originalXhrSend.call(this, body);
            }
            scrubbedXhrSend.__nileCarePartnerHrefScrub = true;
            XMLHttpRequest.prototype.send = scrubbedXhrSend;
        }
    } catch (error) {}
}
`

const CARE_PARTNER_LINK_TOKEN_PATTERN = /(\/care-partner\/l\/)[^\/?#\s"'<>)]+/gi
const CARE_PARTNER_API_TOKEN_PATTERN = /(\/api\/external\/care_partner_requests\/)[^\/?#\s"'<>)]+/gi

/**
 * Replaces care-partner link tokens in `text` with the same placeholder the inline script uses, for telemetry
 * sent outside Datadog (Segment page properties, client-error events, etc.).
 */
export function redactCarePartnerLinkTokens(text: string): string {
    return text
        .replace(CARE_PARTNER_LINK_TOKEN_PATTERN, '$1[redacted]')
        .replace(CARE_PARTNER_API_TOKEN_PATTERN, '$1[redacted]')
}

/**
 * Segment source middleware that redacts care-partner link tokens from page properties and the snippet's
 * buffered `u`/`p` page context before the event leaves the browser.
 */
export function getSegmentCarePartnerScrubMiddlewareSnippet(): string {
    return String.raw`analytics.addSourceMiddleware(function(chain){var payload=chain.payload;var next=chain.next;function redact(v){return typeof v==='string'?v.replace(/(\/care-partner\/l\/)[^\/?#\s"'<>)]+/gi,'$1[redacted]').replace(/(\/api\/external\/care_partner_requests\/)[^\/?#\s"'<>)]+/gi,'$1[redacted]'):v}function scrubHolder(holder,keys){if(!holder)return;for(var i=0;i<keys.length;i++){var key=keys[i];if(typeof holder[key]==='string')holder[key]=redact(holder[key])}}var obj=payload.obj;if(obj){scrubHolder(obj.properties,['path','url','referrer','page']);if(obj.context){scrubHolder(obj.context.page,['path','url','referrer']);scrubHolder(obj.context,['page','path','url','referrer'])}}next(payload)});`
}

type TelemetryEvent = Record<string, any>

/** Evaluates the inline script so tests exercise exactly what the browser runs. */
export function createCarePartnerTelemetryScrubbers(): {
    scrubRumEvent: (event: TelemetryEvent) => void
    scrubLogEvent: (log: TelemetryEvent) => void
    installSessionReplayHrefScrub: () => void
} {
    return new Function(
        `${CARE_PARTNER_TELEMETRY_SCRUB_SCRIPT}; return { scrubRumEvent: nileScrubCarePartnerRumEvent, scrubLogEvent: nileScrubCarePartnerLogEvent, installSessionReplayHrefScrub: nileInstallCarePartnerSessionReplayHrefScrub };`,
    )()
}
